import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  CircularProgress,
  Paper,
  Grid,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  AccessTime as ClockIcon,
  LocationOn as LocationIcon,
  LocalOffer as TagIcon,
  EventBusy as EventBusyIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { fetchApiData } from '../../../store/slicers/apiSlicer.js';

const EventsVisitor = () => {
  const dispatch = useDispatch();
  const [events, setEvents] = useState([]);

  const { data, isLoading } = useSelector((state) => ({
    data: state.apiData?.data?.eventsUpcoming,
    isLoading: state.apiData?.loading?.eventsUpcoming
  }));

  useEffect(() => {
    dispatch(fetchApiData({ url: 'event/upcoming?limit=6', itemKey: 'eventsUpcoming' }));
  }, [dispatch]);

  useEffect(() => {
    if (data?.data) {
      setEvents(data.data);
    }
    console.log("Events data:", data);
  }, [data]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      day: date.getDate(),
      month: date.toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase(),
    };
  };

  if (isLoading) {
    return (
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3, pb: 2, borderBottom: '3px solid #10b981' }}>
          <CalendarIcon sx={{ fontSize: 32, color: '#10b981' }} />
          <Typography variant="h5" fontWeight={700} color="#1e293b">
            Les Événements 
          </Typography>
        </Stack>
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress sx={{ color: '#10b981' }} />
          <Typography sx={{ mt: 2, color: '#94a3b8' }}>
            Chargement des événements...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (!events || events.length === 0) {
    return (
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3, pb: 2, borderBottom: '3px solid #10b981' }}>
          <CalendarIcon sx={{ fontSize: 32, color: '#10b981' }} />
          <Typography variant="h5" fontWeight={700} color="#1e293b">
            Événements à Venir
          </Typography>
        </Stack>
        <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
          <EventBusyIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
          <Typography>
            Aucun événement programmé pour le moment
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
      <Stack 
        direction="row" 
        spacing={2} 
        alignItems="center" 
        sx={{ mb: 3, pb: 2, borderBottom: '3px solid #10b981' }}
      >
        <CalendarIcon sx={{ fontSize: 32, color: '#10b981' }} />
        <Typography variant="h5" fontWeight={700} color="#1e293b" sx={{ flex: 1 }}>
          Événements à Venir
        </Typography>
        <Chip 
          label={events.length} 
          sx={{ 
            bgcolor: '#10b981', 
            color: 'white',
            fontWeight: 600 
          }} 
        />
      </Stack>

      <Grid container spacing={2}>
        {events.map((event, index) => {
          const eventDate = formatDate(event.date);
          return (
            <Grid item xs={12} sm={6} md={4} key={event.id || index}>
              <Card
                sx={{
                  height: '100%',
                  position: 'relative',
                  background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                  border: '2px solid #d1fae5',
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  overflow: 'visible',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 28px rgba(16, 185, 129, 0.2)',
                    borderColor: '#10b981',
                    '& .event-overlay': {
                      opacity: 1,
                    },
                    '& .arrow-icon': {
                      transform: 'translateX(8px)',
                    },
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    p: 1.5,
                    borderRadius: 2,
                    textAlign: 'center',
                    minWidth: 70,
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                    zIndex: 2,
                  }}
                >
                  <Typography variant="h5" fontWeight={700} lineHeight={1}>
                    {eventDate.day}
                  </Typography>
                  <Typography variant="caption" fontWeight={600} sx={{ letterSpacing: 0.5 }}>
                    {eventDate.month}
                  </Typography>
                </Box>

                <CardContent sx={{ pr: 12 }}>
                  <Typography 
                    variant="h6" 
                    fontWeight={600} 
                    color="#1e293b"
                    sx={{ mb: 1.5, lineHeight: 1.4 }}
                  >
                    {event.title}
                  </Typography>

                  {event.description && (
                    <Typography 
                      variant="body2" 
                      color="#475569"
                      sx={{ 
                        mb: 2, 
                        lineHeight: 1.6,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {event.description}
                    </Typography>
                  )}

                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ClockIcon sx={{ fontSize: 16, color: '#10b981' }} />
                      <Typography variant="body2" color="#64748b">
                        {event.time}
                      </Typography>
                    </Stack>

                    {event.location && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LocationIcon sx={{ fontSize: 16, color: '#10b981' }} />
                        <Typography variant="body2" color="#64748b">
                          {event.location}
                        </Typography>
                      </Stack>
                    )}

                    {event.category && (
                      <Chip
                        icon={<TagIcon sx={{ fontSize: 14 }} />}
                        label={event.category}
                        size="small"
                        sx={{
                          bgcolor: 'white',
                          color: '#059669',
                          fontWeight: 500,
                          width: 'fit-content',
                          mt: 1,
                        }}
                      />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );
};

export default EventsVisitor;